/**
 * Browser notification service for Shop Hero Dashboard
 * Handles permission requests and notification display when app is not in focus
 */

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  requireInactive?: boolean;
}

class NotificationService {
  private static instance: NotificationService;

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Check if browser supports notifications
   */
  public isSupported(): boolean {
    return "Notification" in window;
  }

  /**
   * Get current permission status
   */
  public getPermission(): NotificationPermission {
    if (!this.isSupported()) {
      return "denied";
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      return "denied";
    }

    if (this.getPermission() === "granted") {
      return "granted";
    }

    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.warn("Failed to request notification permission:", error);
      return "denied";
    }
  }

  /**
   * Check if the current tab/window is visible
   */
  public isTabVisible(): boolean {
    return document.visibilityState === "visible";
  }

  /**
   * Show a browser notification
   */
  public async show(
    options: NotificationOptions
  ): Promise<Notification | null> {
    // Check if we should only show when tab is inactive
    if (options.requireInactive && this.isTabVisible()) {
      return null;
    }

    // Check support and permission
    if (!this.isSupported() || this.getPermission() !== "granted") {
      console.warn("Notifications not supported or permission not granted");
      return null;
    }

    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || "/favicon.ico",
        tag: options.tag,
        requireInteraction: false,
        silent: false,
      });

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Focus window when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return notification;
    } catch (error) {
      console.error("Failed to show notification:", error);
      return null;
    }
  }

  /**
   * Show a customer update notification (convenience method)
   */
  public async showCustomerUpdateNotification(
    customerName: string,
    carInfo: string
  ): Promise<Notification | null> {
    return this.show({
      title: "Customer Update Required",
      body: `${customerName} - ${carInfo}`,
      tag: "customer-update",
      requireInactive: true,
    });
  }
}

export const notificationService = NotificationService.getInstance();
